import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { getInsurancePlans } from "../APIs/dropdownApi";
import {
  createPatientInsurance,
  updatePatientInsurance,
} from "../APIs/patientInsuranceApi";

export default function AddEditInsuranceModal({
  patientId,
  insurance,
  usedCategories,
  onClose,
  onSaved,
}) {
  const isEdit = !!insurance?.id;

  // Determine available category options
  const allCategories = ["PRIMARY", "SECONDARY"];
  const otherUsed = usedCategories.filter((c) => c !== insurance?.category);
  const availableCategories = allCategories.filter(
    (c) => !otherUsed.includes(c),
  );
  const autoCategory =
    availableCategories.length === 1 ? availableCategories[0] : null;

  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [form, setForm] = useState({
    planId: insurance?.planId ?? "",
    planName: insurance?.planName ?? "",
    planType: "",
    memberId: insurance?.memberId ?? "",
    groupId: insurance?.groupId ?? "",
    coverageStart: insurance?.coverageStart ?? "",
    coverageEnd: insurance?.coverageEnd ?? "",
    category: insurance?.category ?? autoCategory ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getInsurancePlans()
      .then((data) => {
        setPlans(data);
        if (insurance?.planId) {
          const matched = data.find((p) => p.planId === insurance.planId);
          if (matched) setSelectedPlan(matched);
        }
      })
      .catch(() => {});
  }, [insurance?.planId]);

  const handlePlanSelect = (e) => {
    const plan = plans.find((p) => p.planName === e.target.value);
    if (!plan) {
      setSelectedPlan(null);
      setForm((f) => ({ ...f, planId: "", planName: "", planType: "" }));
      return;
    }
    setSelectedPlan(plan);
    setForm((f) => ({
      ...f,
      planId: plan.planId,
      planName: plan.planName,
      planType:
        plan.planTypes?.length === 1
          ? (plan.planTypes[0].typeName ?? plan.planTypes[0].planType ?? "")
          : "",
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.planId) {
      setError("Please select an insurance plan");
      return;
    }
    if (!form.memberId.trim()) {
      setError("Member ID is required");
      return;
    }
    if (!form.category) {
      setError("Please select a category");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const payload = {
        planId: form.planId,
        memberId: form.memberId,
        groupId: form.groupId || null,
        coverageStart: form.coverageStart || null,
        coverageEnd: form.coverageEnd || null,
        category: form.category,
        status: insurance?.status ?? "PENDING",
      };

      let saved;
      if (isEdit) {
        saved = await updatePatientInsurance(patientId, insurance.id, payload);
      } else {
        saved = await createPatientInsurance(patientId, payload);
      }
      onSaved(saved);
    } catch (err) {
      setError(err?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const planTypes = selectedPlan?.planTypes ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">
            {isEdit ? "Edit Insurance" : "Add Insurance"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            {isEdit ? (
              <div className="px-3 py-2 border rounded-lg bg-gray-50 text-sm text-gray-600">
                {form.category}
              </div>
            ) : (
              <select
                name="category"
                value={form.category}
                onChange={handleChange}
                disabled={availableCategories.length === 1}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-50 disabled:text-gray-600"
              >
                <option value="">Select category...</option>
                {availableCategories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Plan Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Insurance Plan
            </label>
            <select
              value={form.planName}
              onChange={handlePlanSelect}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">Select plan...</option>
              {plans.map((p) => (
                <option key={p.planId} value={p.planName}>
                  {p.planName}
                </option>
              ))}
            </select>
          </div>

          {/* Plan Type */}
          {planTypes.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plan Type
              </label>
              <select
                name="planType"
                value={form.planType}
                onChange={handleChange}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Select type...</option>
                {planTypes.map((t) => {
                  const label = t.typeName ?? t.planType ?? "";
                  return (
                    <option key={t.id} value={label}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          {/* Member ID + Group ID */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Member ID
              </label>
              <input
                name="memberId"
                value={form.memberId}
                onChange={handleChange}
                placeholder="e.g. M123456"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Group ID
              </label>
              <input
                name="groupId"
                value={form.groupId}
                onChange={handleChange}
                placeholder="e.g. G7890"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>

          {/* Coverage Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Coverage Start
              </label>
              <input
                type="date"
                name="coverageStart"
                value={form.coverageStart}
                onChange={handleChange}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Coverage End
              </label>
              <input
                type="date"
                name="coverageEnd"
                value={form.coverageEnd}
                onChange={handleChange}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm border rounded-lg text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-40"
            >
              {saving ? "Saving..." : "Save & Continue"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
